import React, { useContext, useEffect, useState } from 'react';
import { MenuItem } from '../../models/MenuItem';
import { Button } from './Buttons/Button';
import { StyleContext } from '../../providers/StyleContextProvider';
import { useMediaQuery } from 'react-responsive';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DropdownItem } from './DropdownItem';
import useErrorPage from '../../hooks/useErrorPage';
import { getRecruiterQuotaDetails } from '../../services/RecruiterService';
import { RecruiterQuotaDetails } from '../../models/RecruiterQuotaDetails';
import { ReactComponent as Location } from '../../assets/svg/location.svg';
import { ReactComponent as Users } from '../../assets/svg/users.svg';
import { ReactComponent as Arrow } from '../../assets/svg/drop_down.svg';
import { ReactComponent as Plus } from '../../assets/svg/plus.svg';
import { ReactComponent as MobileMenu } from '../../assets/svg/mobile_menu.svg';
import { ReactComponent as Lines } from '../../assets/svg/lines.svg';
import { ReactComponent as SettingsIcon } from '../../assets/svg/settings.svg';
import { CommonTexts as Texts } from '../../helpers/LayoutTexts';
import { MobileMenuDrawer } from './MobileMenuDrawer';
import { XpressJobsTitle } from './XpressJobsTitle';
import { AuthContext } from '../../providers/AuthContextProvider';
import { LoggedRecruiter } from '../../models/LoggedRecruiter';
import { OrganizationDetailsLoader } from '../Loaders/OrganizationDetailsLoader';
import { QuatoDetailsLoader } from '../Loaders/QuatoDetailsLoader';
import { CheckoutTypes } from "../../enums/CheckoutTypes";
import { Invoice } from '../../models/Invoice';
import { UserType } from '../../enums/UserType';

interface Props {    
    isSubOrganization?: (status: boolean) => void;
    hasPendingInvoice?: (invoice?: Invoice) => void;
    onQuotaDetailsLoaded?: (quotaDetails: RecruiterQuotaDetails) => void;
}

export const RecruiterMenu: React.FC<Props> = (props) => {
    const authContext = useContext(AuthContext);    
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();
    const styleContext = useContext(StyleContext);
    const navigate = useNavigate();
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("recruiterMenu")); 
    const [quotaDetails, setQuotaDetails] = useState<RecruiterQuotaDetails>();    
    const [selectedMenu, setSelectedMenu] = useState<number>(-1);   
    const [openMobileMenu, setOpenMobileMenu] = useState<boolean>(false);  
    const [openSideMenu, setOpenSideMenu] = useState<boolean>(false); 
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [isSubOrganization, setIsSubOrganization] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const location = useLocation();
    const jobsMenuIndex = 0;
    const settingsMenuIndex = 3;
    const helpMenuIndex = 5;
    
    useEffect(() => {   
        setStyles(styleContext.getComponentStyle('recruiterMenu'));                 
    }, [isMobile]);

    useEffect(() => {                                                                                          
        getQuotaDetails();                              
    }, []);

    useEffect(() => {
        if (location.pathname.includes('/recruiter/vacancy')) {
            setSelectedMenu(jobsMenuIndex);
        }
        else if (location.pathname.startsWith("/recruiter/settings")) {
            setSelectedMenu(settingsMenuIndex);
        }
        else if (location.pathname.includes('/recruiter/helpAndSupport')) {
            setSelectedMenu(helpMenuIndex);
        }
    }, [location.pathname]);

    async function getQuotaDetails() {                
        await getRecruiterQuotaDetails(recruiter!.recruiterId, recruiter!.organizationId, recruiter!.token)
        .then((recruiterQuotaDetails: RecruiterQuotaDetails) => {            
            setQuotaDetails(recruiterQuotaDetails);            
            setIsSubOrganization(recruiterQuotaDetails.isSubOrganization);
            if (props.isSubOrganization) {
                props.isSubOrganization(recruiterQuotaDetails.isSubOrganization);
            }
            if (props.hasPendingInvoice) {
                props.hasPendingInvoice(recruiterQuotaDetails.pendingInvoice);
            }
            if (props.onQuotaDetailsLoaded) {
                props.onQuotaDetailsLoaded(recruiterQuotaDetails);
            }
            setLoading(false);            
        })
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        });      
    }
        
    const handleMobileMenuToggle = () => {        
        setOpenMobileMenu((prev) => !prev);
    }

    const handleSideMenuToggle = () => {        
        setOpenSideMenu((prev) => !prev);
    }

    const handlePostJob = () => {
        navigate("/recruiter/vacancy/addedit?tab=basicDetails");
    }

    const mobileMenuItems: MenuItem[] = [
        { isList: false, item: <Link to={'/'} style={styles.mobileMenu.label}>{"Home"}</Link> },
        { isList: false, item: <Link to={'/for-recruiters#content'} style={styles.mobileMenu.label}>{"Features"}</Link> },
        { isList: false, item: <Link to={'/for-recruiters#pricing-table'} style={styles.mobileMenu.label}>{"Pricing"}</Link> },
        { isList: false, item: <Link to={'/for-recruiters#faq'} style={styles.mobileMenu.label}>{"FAQs"}</Link> },
        { isList: true, label: "More", items: [] = [
            { item: 
                <Link to={"/testimonials/recruiters"} style={styles.mobileMenu.dropdownLabel}>
                    <DropdownItem styles={styles.mobileMenu.dropdownItem}>{"Testimonials"}</DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/press"} style={styles.mobileMenu.dropdownLabel}>
                    <DropdownItem styles={styles.mobileMenu.dropdownItem}>{"Press Releases"}</DropdownItem>
                </Link>                    
            },
            { item:
                <Link to={"/home/preferredPartners"} style={styles.mobileMenu.dropdownLabel}>
                    <DropdownItem styles={styles.mobileMenu.dropdownItem}>{"Partners"}</DropdownItem>
                </Link>                    
            },
            { item:
                <Link to={"/home/differentlyabledfriendlyemployment"} style={styles.mobileMenu.dropdownLabel}>
                    <DropdownItem styles={styles.mobileMenu.dropdownItem}>{"Differently Abled Friendly Employment"}</DropdownItem>
                </Link>                    
            }]
        }
    ]

    const sideMenuItems: MenuItem[] = [        
        { isList: true, label: "Jobs", items:[] = [
            { item: 
                <Link to={"/recruiter/vacancy?page=1&status=ALL"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"All"}</DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/recruiter/vacancy?page=1&status=LIVE"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>
                        {"Live"}<span style={styles.menu.activeCircle}></span>
                    </DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/recruiter/vacancy?page=1&status=PROMOTED"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"Promoted"}</DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/recruiter/vacancy?page=1&status=PENDING"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"Pending Approval"}</DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/recruiter/vacancy?page=1&status=ARCHIVE"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"Expired (Archive)"}</DropdownItem>
                </Link>                
            }] 
        },     
        { isList: false, item: <Link to={'/recruiter/candidate'} style={styles.menu.label}>{"Candidate Pool"}</Link> },  
        { isList: false, item: <Link to={'/recruiter/stats'} style={styles.menu.label}>{"Statistics"}</Link> },              
        { isList: true, label: "Settings", items:[] = [
            { item: 
                <Link to={"/recruiter/settings/account"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"Account"}</DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/recruiter/settings/organization"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"Organization"}</DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/recruiter/settings/integration"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"Integration"}</DropdownItem>
                </Link>                
            }] 
        }, 
        { isList: false, item: <Link to={'/for-recruiters#pricing-table'} style={styles.menu.label}>{"Pricing"}</Link> }, 
        { isList: true, label: "Help & Support", items:[] = [
            { item: 
                <Link to={"/recruiter/helpAndSupport/FAQs"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"FAQs"}</DropdownItem>
                </Link>                
            },
            { item: 
                <Link to={"/recruiter/helpAndSupport/ContactUs"} style={styles.menu.label}>
                    <DropdownItem styles={styles.menu.item}>{"Contact Us"}</DropdownItem>
                </Link>                
            }] 
        }
    ]  
    
    const handleMenuClick = (menuIndex: number) => {
        if(selectedMenu === menuIndex) {
            setSelectedMenu(-1);
        }
        else {
            setSelectedMenu(menuIndex);
        }
    }

    const handleSettingsClick = () => {
        navigate("/recruiter/settings/account");
    };

    const handleAddButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate("/recruiter/vacancy/addedit?tab=basicDetails");
    };

    const toPurchase = () => {
        if(quotaDetails?.pendingInvoice) {
            navigate(`/recruiter/invoice/pay/${quotaDetails.pendingInvoice.refId}`);
        }
        else {
            navigate('/recruiter/vacancy/checkout', {
                state: {
                    checkoutType: CheckoutTypes.CVQuota
                }
            })
        }
    }

    return (
        <>
            {/* This is loading only mobile platform */}
            <MobileMenuDrawer open={openMobileMenu} toggle={handleMobileMenuToggle} menuItems={mobileMenuItems} recruiter={recruiter} />               
            <div style={{...styles.wrapper, ...(openSideMenu ? styles.wrapper.open : styles.wrapper.close)}}>        
                <div style={styles.leftWrapper} className={"disableScrollBar"}>                     
                    <div style={styles.title}><XpressJobsTitle style={styles.logo}/></div>
                    <Button style={styles.closeButton} onClick={handleSideMenuToggle}>
                        <Arrow style={styles.closeButtonIcon}/>
                    </Button> 
                    {loading ? <OrganizationDetailsLoader/> :
                    <>
                        <div style={styles.organization.wrapper}>
                            <img style={styles.organization.logo} src={quotaDetails!.organization.hasLogo ? quotaDetails!.organization.logoUri : Texts.defaultCompanyLogo} />
                            <div style={styles.organization.content}>
                                <SettingsIcon style={styles.settingButtonIcon} onClick={handleSettingsClick}/>
                                <div style={styles.organization.name}>{quotaDetails!.organization.organizationName}</div>
                                {quotaDetails!.organization.city && <div style={styles.organization.detailWrapper}>
                                    <Location style={styles.organization.locationIcon}/>
                                    <span style={styles.organization.organizationInfo}>{quotaDetails!.organization.city}</span>
                                </div>}
                                {quotaDetails!.organization.employees && <div style={styles.organization.detailWrapper}>
                                    <Users style={styles.organization.userIcon}/>
                                    <span style={styles.organization.organizationInfo}>{quotaDetails!.organization.employees}</span>
                                </div>}
                            </div>           
                        </div> 
                    </>}                         
                    <ul style={styles.menu.wrapper}>
                        {sideMenuItems.map((menuItem, menuIndex) => <li key={menuIndex}>
                            {menuItem.isList ? <div onClick={() => handleMenuClick(menuIndex)} style={styles.menu.list}> 
                                <div style={styles.menu.buttonWrapper}>
                                    <div style={{...styles.menu.labelWrapper, ...(selectedMenu === menuIndex && styles.menu.labelWrapper.hovered)}}>
                                        <span>{menuItem.label}</span>
                                        <Arrow style={{...styles.menu.labelIcon, ...(selectedMenu === menuIndex && styles.menu.labelIcon.hovered)}}/>
                                    </div>
                                    {!isSubOrganization && menuIndex === 0 && <Button style={styles.menu.addButton} onClick={handleAddButtonClick}>
                                        <Plus style={styles.menu.addIcon}/>
                                    </Button>} 
                                </div>           
                                <div style={selectedMenu === menuIndex ? styles.menu.hover : styles.menu.leave}>
                                    {menuItem.items!.map((item, itemIndex) =>
                                        <div key={itemIndex}>
                                            {item.item}                                            
                                        </div>
                                    )}
                                </div>
                            </div> : 
                            <div style={styles.menu.labelWrapper}>{menuItem.item}</div>}
                        </li>)}
                    </ul>  
                    {loading ? <QuatoDetailsLoader/> : ((quotaDetails?.hasEmpty || quotaDetails?.hasExpired) ? <div style={styles.quota.emptyWrapper}>
                        <div>{"No CV package activated"}</div>
                        {!isSubOrganization && <Button style={styles.quota.purchaseButton} onClick={toPurchase}>{"PURCHASE NEW PACK"}</Button>}
                    </div>
                    : <div style={styles.quota.wrapper}>
                        <div style={styles.quota.row}>
                            <div style={styles.quota.packageType}>{"Package & Billing"}</div>
                            <div style={styles.quota.packageValue}>{quotaDetails?.quotaType}</div>
                        </div>
                        <div style={styles.quota.row}>
                            <div style={styles.quota.quotaDetail}>{quotaDetails?.quotaType === "Monthly" ? "Time" : "CV Count"}</div>
                            <div style={styles.quota.quotaDetail}>
                                {quotaDetails?.quotaType === "Monthly" ? (quotaDetails?.remainDayCount + (quotaDetails?.remainDayCount > 1 ? " days" : " day") + " left") 
                                : (quotaDetails?.remainCVCount + (quotaDetails?.remainCVCount! > 1 ? " slots" : " slot") + " left")}
                            </div>
                        </div>
                        <div style={styles.quota.progressBar}>
                            <div style={{...styles.quota.progressFilter, ...{width: `${100 - quotaDetails?.remainPercentage!}%`}}}></div>
                        </div>
                        <div style={styles.quota.row}>
                            {quotaDetails?.quotaType === "Monthly" && <div style={styles.quota.expiry}>
                                <div style={styles.quota.expiryText}>{"Expires on"}</div>
                                <div style={styles.quota.expiryDate}>{quotaDetails?.expireDate}</div>
                            </div>}
                            {!isSubOrganization && <Button style={styles.quota.topUpButton} onClick={toPurchase}>{"TOP UP"}</Button>}
                        </div>
                    </div>)}                        
                </div>     
                {/* {This is back layer of the drawer} */}
                <div style={styles.rightWrapper} onClick={handleSideMenuToggle}></div>                       
            </div>            
            <>
                {/* This is loading only mobile platform */}
                {isMobile && (loading ? <OrganizationDetailsLoader/> :
                <>
                    <div style={{...styles.topMenu, ...(openSideMenu && styles.topMenu.transparent)}}>
                        <img style={styles.organization.logo} src={quotaDetails!.organization.hasLogo ? quotaDetails!.organization.logoUri : Texts.defaultCompanyLogo} />
                        <div style={styles.organization.content}>
                            <div style={styles.organization.name}>
                                <div>{quotaDetails!.organization.organizationName}</div>
                                <div onClick={handleMobileMenuToggle}><MobileMenu/></div>
                            </div>
                            {quotaDetails!.organization.city && <div style={styles.organization.detailWrapper}>
                                <Location style={styles.organization.locationIcon}/>
                                <span style={styles.organization.detailText}>{quotaDetails!.organization.city}</span>
                            </div>}
                            {quotaDetails!.organization.employees && <div style={styles.organization.detailWrapper}>
                                <Users style={styles.organization.userIcon}/>
                                <span style={styles.organization.detailText}>{quotaDetails!.organization.employees}</span>
                            </div>}
                        </div>
                    </div>
                </>)}    
                <div style={styles.actions}>
                    <div style={styles.actions.lines} onClick={handleSideMenuToggle}><Lines style={styles.actions.lines.icon}/></div>
                    <div style={styles.actions.arrow} onClick={handleSideMenuToggle}><Arrow style={styles.actions.arrow.icon}/></div>
                    <div style={styles.actions.children}>{props.children}</div>
                </div>
            </>
        </>
    );
}